import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  BiContextProvidedValue,
  BiData,
  ClickReportButtonBiEvent,
  ClickShareBiEvent,
  ClickSocialShareBiEvent,
  EditWithMuroBiEvent,
  MoreMenuClickBiEvent,
  UnlistItemConfirmBiEvent,
} from '@wix/da-bi/pkg/events';
import NativeDropdown from '@wix/da-ds/pkg/Dropdown/NativeDropdown';
import { useVerification } from '../../../../VerificationPopup/withVerification';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import {
  OnUnpublishCompleteAction,
  type UnpublishDeviationPayload,
} from '../../../../redux/deviations/actionCreators';
import { useCopyDeviationLinkClick } from '../../../../utils/hooks/useCopyLinkClick';
import { useUnpublishFlow } from '../../../../utils/hooks/useUnpublishFlow';
import { useElementPlacementForArea } from '../../../ElementPlacementContext';
import { StandardDeviationElementPlacement } from '../../../ElementPlacementContext/variants/standard/types';
import { StandardDeviationElementPlacementContext } from '../../../ElementPlacementContext/variants/standard/context';
import { UseActionsWithPopupsReturnType } from '../hooks';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { usePushModal } from '../../../../utils/hooks/usePushModal';
import { ModalType } from '../../../../Modals/redux/types';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';

import s from './MoreMenuMobileDropdown.scss';

export interface Props {
  deviation: PapiDeviation;
  showReportModal: (deviation: PapiDeviation) => void;
  shareDeviationToPost: (deviation: PapiDeviation) => void;
  unpublishDeviation: (payload: UnpublishDeviationPayload) => void;
  actionsWithPopovers: UseActionsWithPopupsReturnType;
}

const MoreMenuMobileDropdown: React.FC<Props> = ({
  deviation,
  showReportModal,
  shareDeviationToPost,
  unpublishDeviation,
  actionsWithPopovers,
}) => {
  const hasMobileFullFeature = useFlag('mobile_muro_full');
  const { t } = useTranslation();
  const placementMap = useElementPlacementForArea(
    StandardDeviationElementPlacement.PrimaryBarMenu,
    StandardDeviationElementPlacementContext
  );

  const pushMuroModalMobile = usePushModal(ModalType.MINI_MOBILE_MURO);
  const { MobileComponent } = useContext(MuroDepContext);

  const { onCopyLinkClick } = useCopyDeviationLinkClick(deviation);
  const initiateUnpublishFlow = useUnpublishFlow({
    deviation,
    onCompleteAction: OnUnpublishCompleteAction.ReloadStashPage,
    confirmBtnBiData: BiData<UnlistItemConfirmBiEvent>({
      evid: 359,
      itemid: deviation.deviationId,
      typeid: getDeviationResourceType(deviation),
      sectionname: 'action_bar',
    }),
  });
  const { restrictToVerifiedAndLoggedIn } = useVerification();
  const restrictedShareToPost = restrictToVerifiedAndLoggedIn(
    ReferralType.SHARE_TO_POST,
    'submitstatus',
    () => shareDeviationToPost(deviation)
  );

  const onSelect = value => {
    const selectedActionWithPopover = Object.values(actionsWithPopovers).find(
      actionWithPopover =>
        actionWithPopover && actionWithPopover.action.value === value
    );
    if (selectedActionWithPopover) {
      selectedActionWithPopover.action.onClick &&
        selectedActionWithPopover.action.onClick(undefined);
    } else {
      switch (value) {
        case 'copylink':
          onCopyLinkClick();
          break;
        case 'sharetopost':
          restrictedShareToPost();
          break;
        case 'report':
          showReportModal(deviation);
          break;
        case 'unpublish':
          initiateUnpublishFlow();
          break;
        case 'draw':
          if (MobileComponent) {
            pushMuroModalMobile({
              mobileComponent: MobileComponent,
              deviation: deviation,
            });
          }
          break;
        default:
      }
    }
  };

  return (
    <NativeDropdown
      className={s['root']}
      onSelect={onSelect}
      openBiData={BiData<MoreMenuClickBiEvent>({
        evid: 417,
        typeid: getDeviationResourceType(deviation),
        itemid: deviation.deviationId,
        sectionname: 'deviation',
      })}
      items={[
        actionsWithPopovers.addToGroup?.action,
        actionsWithPopovers.privateCollection?.action,
        placementMap.share && {
          value: 'copylink',
          label: t('actionbar.copyLink'),
          biData: BiData<ClickShareBiEvent>({
            evid: 355,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            deviationid: deviation.deviationId,
          }),
        },
        placementMap.share && {
          value: 'sharetopost',
          label: t('actionbar.shareToPost'),
          biData: BiData<ClickSocialShareBiEvent>({
            evid: 356,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            social_share: 'da_post',
            deviationid: deviation.deviationId,
          }),
        },
        placementMap.report && {
          value: 'report',
          label: t('actionbar.report'),
          biData: BiData<ClickReportButtonBiEvent>({
            evid: 20,
            modal_version: 2,
            deviationid: deviation.deviationId,
          }),
        },
        placementMap.unpublish && {
          value: 'unpublish',
          label: t('actionbar.unpublish'),
        },
        hasMobileFullFeature &&
          placementMap.draw && {
            value: 'draw',
            label: t('actionbar.draw.edit'),
            biData: BiData<EditWithMuroBiEvent>({
              evid: 926,
              deviationid: deviation.deviationId,
              sectionname: BiContextProvidedValue,
              page_itemid: BiContextProvidedValue,
              page_typeid: BiContextProvidedValue,
            }),
            onClick: () => {},
          },
      ]}
    />
  );
};
MoreMenuMobileDropdown.displayName = 'MoreMenuMobileDropdown';

export default MoreMenuMobileDropdown;
